import React, { useEffect, useState } from "react";
import { getApiUrl, BASE_URL } from "../utils/api";
import { useNavigate } from "react-router-dom";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mixedOffer, setMixedOffer] = useState(null);
  const [bulkCombos, setBulkCombos] = useState([]);

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  const fetchCart = async () => {
    try {
      const res = await fetch(getApiUrl("cart-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();
      if (data.success) setCartItems(data.data || []);
    } catch (err) {
      console.error("Fetch Cart Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const removeCartItem = async (cartId) => {
    try {
      const res = await fetch(getApiUrl("cart-delete"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cartId }),
      });

      const data = await res.json();
      if (data.success) {
        setCartItems((prev) => prev.filter((item) => item._id !== cartId));
      }
    } catch (err) {
      console.error("Remove Error:", err);
    }
  };

  const fetchMixedOffer = async () => {
    try {
      const res = await fetch(getApiUrl("combo-active"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      if (data.success && data.data.length > 0) setMixedOffer(data.data[0]);
    } catch (err) {
      console.error("Mixed Offer Fetch Error:", err);
    }
  };

  const fetchBulkCombos = async () => {
    try {
      const res = await fetch(getApiUrl("bulk-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      if (data.success) setBulkCombos(data.data);
    } catch (err) {
      console.error("Bulk Combo Fetch Error:", err);
    }
  };

  useEffect(() => {
    fetchCart();
    fetchMixedOffer();
    fetchBulkCombos();
  }, []);

  if (loading) return <h5 className="text-center py-5">Loading Cart...</h5>;

  const validItems = cartItems.filter((item) => item.productId);

  if (validItems.length === 0)
    return <h5 className="text-center py-5">Cart is empty 😢</h5>;

  const totalPrice = validItems.reduce(
    (sum, item) => sum + item.quantity * item.productId.price,
    0,
  );

  const allSubcats = validItems.map((i) =>
    i.productId.subcategoryId?.toString(),
  );

  const uniqueSubcatsCount = new Set(allSubcats.filter(Boolean)).size;

let mixedDiscount = 0;
if (mixedOffer && uniqueSubcatsCount >= mixedOffer.minQuantity) {
  mixedDiscount = mixedOffer.discountPerItem * validItems.length;
}

  let bulkFinalPrice = 0;
  const sameSubcategory =
    new Set(allSubcats).size === 1 && allSubcats.length > 0;

  if (sameSubcategory) {
    const bulkCombo = bulkCombos.find(
      (b) =>
        (b.subcategoryId?._id || b.subcategoryId)?.toString() === allSubcats[0],
    );

    if (bulkCombo) {
      const qty = validItems.reduce((sum, i) => sum + i.quantity, 0);

      const slab = bulkCombo.priceSlabs.find(
        (s) => qty >= s.minQuantity && (!s.maxQuantity || qty <= s.maxQuantity),
      );

      bulkFinalPrice = slab ? slab.price : 0;
    }
  }

  const finalPrice =
    bulkFinalPrice > 0 ? bulkFinalPrice : totalPrice - mixedDiscount;

  const offerType =
    bulkFinalPrice > 0 ? "BULK" : mixedDiscount > 0 ? "MIXED" : "";

  const proceedCheckout = async () => {
    if (validItems.length === 0) return;

    const products = validItems.map((item) => ({
      productId: item.productId._id,
      name: item.productId.productName,
      price: item.productId.price,
      size: item.selectedSize,
      qty: item.quantity,
      subtotal: item.quantity * item.productId.price,
    }));

    const bulkDiscount = offerType === "BULK" ? totalPrice - bulkFinalPrice : 0;
    navigate("/bsk-checkout", {
      state: {
        products,
        totalPrice,
        totalDiscount:
          offerType === "MIXED"
            ? mixedDiscount
            : offerType === "BULK"
              ? bulkDiscount
              : 0,
        finalPrice,
        offerType,
      },
    });

    try {
      await fetch(getApiUrl("cart-clear"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });

      setCartItems([]);
    } catch (err) {
      console.error("Cart clear failed", err);
    }
  };

  return (
    <div className="container py-5">
      <h3 className="mb-4">My Cart</h3>

      {validItems.map((item) => (
        <div
          key={item._id}
          className="card p-3 mb-3 shadow-sm border-0 d-flex flex-row align-items-center"
        >
          <img
            src={
              item.productId.mainImage
                ? `${BASE_URL}${item.productId.mainImage}`
                : "/no-image.png"
            }
            alt={item.productId.productName}
            style={{
              width: "110px",
              height: "110px",
              objectFit: "cover",
              borderRadius: "10px",
            }}
          />

          <div className="ms-3 flex-grow-1">
            <h6 className="mb-1">{item.productId.productName}</h6>

            <p className="mb-1">
              Price: ₹{item.productId.price}
              {item.productId.originalPrice && (
                <span className="text-muted text-decoration-line-through ms-2">
                  ₹{item.productId.originalPrice}
                </span>
              )}
            </p>

            <small className="d-block mb-1">
              Size: <strong>{item.selectedSize}</strong>
            </small>

            <small>
              Qty: <strong>{item.quantity}</strong>
            </small>
          </div>

          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => removeCartItem(item._id)}
          >
            Remove
          </button>
        </div>
      ))}

      <hr />

      <div className="card p-3 mb-3 shadow-sm border-0 d-flex flex-row align-items-center">
        <div>
          <h5>Total: ₹{totalPrice}</h5>

          {offerType === "MIXED" && (
            <p className="text-success">Mixed Discount: -₹{mixedDiscount}</p>
          )}

          {offerType === "BULK" && (
            <p className="text-primary">Bulk Price Applied</p>
          )}

          <h4>Final: ₹{finalPrice}</h4>

          <div className="d-flex justify-content-center">
            <button className="btn  btn-success mt-3" onClick={proceedCheckout}>
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
